import { fetchGraphQL, fetchClientGraphQL } from '~/hooks/graphql.js';

const productNodePrice = `
  price(format: FORMATTED)
  salePrice(format: FORMATTED)
  regularPrice(format: FORMATTED)
  type
`;

const getMinimalProductNode = `
  product {
    node {
      productId
      id
      name
      productVendor {
        name
        slug
      }
      featuredImage {
        node {
          sourceUrl(size: THUMBNAIL)
        }
      }
      uri
      defaultCurrency {
        code
        price
        regularPrice
        salePrice
        priceExTax
        regularPriceExTax
        salePriceExTax
      }
      productSaleBadges {
        percentageSaleBadge
        showEndsSoonBadge
        showEndsTodayBadge
      }
      productSubscriptionOptions {
        schemeKey
        duration
        percentageDiscount
        price
        regularPrice
        salePrice
        priceExTax
        regularPriceExTax
        salePriceExTax
      }
      ... on SimpleProduct {
        ${productNodePrice}
        soldIndividually
      }
      ... on VariableProduct {
        ${productNodePrice}
        soldIndividually
      }
      ... on SubscriptionProduct {
        ${productNodePrice}
        addToCartText
        signUpFee
        subscriptionRenewalDate
        soldIndividually
      }
      ... on GroupProduct { ${productNodePrice} }
      ... on ExternalProduct { ${productNodePrice} }
    }
  }
`;

export const variationProductKeys = `
  name
  price(format: FORMATTED)
  salePrice(format: FORMATTED)
  regularPrice(format: FORMATTED)
  defaultCurrency {
    code
    price
    regularPrice
    salePrice
    priceExTax
    regularPriceExTax
    salePriceExTax
  }
  productSaleBadges {
    percentageSaleBadge
    showEndsSoonBadge
    showEndsTodayBadge
  }
  productSubscriptionOptions {
    schemeKey
    duration
    percentageDiscount
    price
    regularPrice
    salePrice
    priceExTax
    regularPriceExTax
    salePriceExTax
  }
  databaseId
  variationName
  variationProductSpecs {
    editionNotes
    extractedSize
    includedIds
    largestZip
    numberOfFiles
    numberOfSounds
  }
  image {
    altText
    mediaItemUrl
  }
`;

// Get current variation details - note singular 'variation' vs 'variations'
const getVariationProductNode = `
  variation {
    node {
      ${ variationProductKeys }
    }
  }
`;

const sharedProductKeys = `
    name
    databaseId
    price(format: FORMATTED)
    regularPrice(format: FORMATTED)
    salePrice(format: FORMATTED)
    defaultCurrency {
      code
      price
      regularPrice
      salePrice
      priceExTax
      regularPriceExTax
      salePriceExTax
      # Numeric variants, for the Umami view_item event only — the display code keeps using the
      # formatted strings above. Deliberately NOT added to getMinimalProductNode: that node sits on
      # hot paths (mini-cart, cart refresh, every add), this query runs once per product page.
      priceFloat
      regularPriceFloat
      salePriceFloat
    }
    productSaleBadges {
      percentageSaleBadge
      showEndsSoonBadge
      showEndsTodayBadge
    }
    productSubscriptionOptions {
      schemeKey
      duration
      percentageDiscount
      price
      regularPrice
      salePrice
      priceExTax
      regularPriceExTax
      salePriceExTax
    }
    formattedContent
    formattedExcerpt
    image {
      altText
      mediaItemUrl
    }
    galleryImages {
      nodes {
        altText
        mediaItemUrl
      }
    }
    productSpecs {
      node {
        filesIncluded
        format
        metatagged
        notMetatagged
        productSoundcloudCode
        soundcloudShortcode
        audioDemos {
          url
          peaksUrl
          duration
          format
          title
          source
          scTrackId
          lufs
          truePeak
        }
        soundsIncluded
        tagline
        totalDuration
        totalSize
        type
      }
    }
    productVendor {
      slug
      name
    }
    productCategories {
      nodes {
        link
        name
        uri
      }
    }
    enqueuedStylesheets {
      nodes {
        id
        handle
        src
      }
    }
    seo {
      canonical
      title
      breadcrumbs {
        text
        url
      }
      metaDesc
      metaRobotsNofollow
      metaRobotsNoindex
      opengraphAuthor
      opengraphDescription
      opengraphImage {
        altText
        mediaItemUrl
      }
      opengraphModifiedTime
      opengraphPublishedTime
      opengraphPublisher
      opengraphSiteName
      opengraphTitle
      opengraphType
      opengraphUrl
    }
    jsonLd
`

const getSimpleProductNode = `
  ... on SimpleProduct {
    ${ sharedProductKeys }
  }
  ... on SubscriptionProduct {
    ${ sharedProductKeys }
  }
  ... on VariableProduct {
    ${ sharedProductKeys }
    variations {
      nodes {
        ${ variationProductKeys }
      }
    }
  }
  ... on GroupProduct {
    ${ sharedProductKeys }
  }
  ... on ExternalProduct {
    ${ sharedProductKeys }
  }
`;

const getProductDataQuery = `
  query GetProductDataQuery($uri: String!) {
    nodeByUri(uri: $uri) {
      ${ getSimpleProductNode }
    }
    productTypeByUri(uri: $uri) {
      productId
    }
  }
`;

const getProductTypeQuery = `
  query GetProductTypeQuery($uri: String!) {
    productTypeByUri(uri: $uri) {
      status
      productId
      isPasswordProtected
    }
  }
`;

async function getProductTypeByUri(uri) {
  return fetchGraphQL(getProductTypeQuery, { uri });
}

async function getProductById(productId) {
  return await fetchClientGraphQL(`
    query Product($productId: ID!) {
      product(id: $productId, idType: DATABASE_ID) {
        ${ getSimpleProductNode }
        isPreview
        status
      }
    }
  `, { productId });
}

async function getProductPreviewById(productId) {
  return await fetchClientGraphQL(`
    query ProductPreview ($productId: Int!, $isPreview: Boolean) {
      productPreview(id: $productId, preview: $isPreview) {
        ${ getSimpleProductNode }
        isPreview
        status
      }
    }
  `, { productId });
}

async function getPrivateProductById({ productId, password }) {
  return await fetchClientGraphQL(`
    query ProductPrivate ($productId: Int!, $password: String) {
      productPrivate(id: $productId, password: $password) {
        ${ getSimpleProductNode }
        isPreview
        status
      }
    }
  `, { productId, password });
}

const fetchProductData = (uri) => {
  return fetchGraphQL(getProductDataQuery, { uri });
};

export {
  getMinimalProductNode,
  getVariationProductNode,
  getSimpleProductNode,
  getProductDataQuery,
  fetchProductData,
  getProductById,
  getProductPreviewById,
  getPrivateProductById,
  getProductTypeByUri,
}
